/**
 * QuizTaker component - interactive quiz taking interface
 */

import React, { useState, useEffect } from 'react';
import './QuizTaker.css';

interface Question {
    id: number;
    quizId: number;
    questionType: number;
    question: string;
    answer: string;
    answer_choices_json: string;
    answerExplanation?: string | null;
}

interface QuizTakerProps {
    quizId: number;
}

export default function QuizTaker({ quizId }: QuizTakerProps) {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [userAnswer, setUserAnswer] = useState('');
    const [showFeedback, setShowFeedback] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [quizCompleted, setQuizCompleted] = useState(false);
    const [animateProgress, setAnimateProgress] = useState(false);
    const [randomizedChoices, setRandomizedChoices] = useState<string[]>([]);
    const [quizHistoryId, setQuizHistoryId] = useState<number | null>(null);
    const [finalScore, setFinalScore] = useState<number | null>(null);

    useEffect(() => {
        const abortController = new AbortController();
        let sessionStarted = false;

        const fetchQuestionsAndStartSession = async () => {
            try {
                const response = await fetch(`/api/quizzes/${quizId}`, {
                    signal: abortController.signal,
                });
                if (response.ok) {
                    const data = await response.json();

                    // Randomize question order using Fisher-Yates shuffle
                    const shuffled = [...data.questions];
                    for (let i = shuffled.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                    }
                    setQuestions(shuffled);

                    // Start quiz history session only if not aborted
                    if (!abortController.signal.aborted && !sessionStarted) {
                        sessionStarted = true;
                        const historyResponse = await fetch(
                            '/api/quiz-history/start',
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({ quizId }),
                                signal: abortController.signal,
                            }
                        );

                        if (historyResponse.ok) {
                            const historyData = await historyResponse.json();
                            setQuizHistoryId(historyData.quizHistoryId);
                        }
                    }
                }
            } catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    console.log('Fetch aborted');
                } else {
                    console.error('Failed to fetch questions:', error);
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchQuestionsAndStartSession();

        // Cleanup function to abort fetch if component unmounts
        return () => {
            abortController.abort();
        };
    }, [quizId]);

    const currentQuestion = questions[currentQuestionIndex];

    // Randomize multiple choice options when question changes
    useEffect(() => {
        if (currentQuestion && currentQuestion.questionType === 1) {
            try {
                const choices = JSON.parse(currentQuestion.answer_choices_json);
                if (Array.isArray(choices) && choices.length > 0) {
                    // Shuffle the array using Fisher-Yates algorithm
                    const shuffled = [...choices];
                    for (let i = shuffled.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                    }
                    setRandomizedChoices(shuffled);
                }
            } catch (error) {
                console.error('Failed to parse answer choices:', error);
                setRandomizedChoices([]);
            }
        } else {
            setRandomizedChoices([]);
        }
    }, [currentQuestionIndex, currentQuestion]);

    // Trigger progress bar animation during feedback
    useEffect(() => {
        if (showFeedback) {
            // Reset animation state first
            setAnimateProgress(false);
            // Use setTimeout to trigger reflow and start animation
            setTimeout(() => {
                setAnimateProgress(true);
            }, 10);
        } else {
            setAnimateProgress(false);
        }
    }, [showFeedback]);

    const handleSubmitAnswer = async () => {
        if (!currentQuestion) return;

        let correct = false;

        if (currentQuestion.questionType === 1) {
            // Multiple Choice: compare with the first element in answer_choices_json
            try {
                const choices = JSON.parse(currentQuestion.answer_choices_json);
                const correctAnswer = choices[0]; // First choice is always the correct answer
                correct = userAnswer.trim() === correctAnswer.trim();
            } catch (error) {
                console.error('Failed to parse answer choices:', error);
                correct =
                    userAnswer.toLowerCase().trim() ===
                    currentQuestion.answer.toLowerCase().trim();
            }
        } else {
            // Short Answer or True/False: compare with answer field
            correct =
                userAnswer.toLowerCase().trim() ===
                currentQuestion.answer.toLowerCase().trim();
        }

        setIsCorrect(correct);
        setShowFeedback(true);

        // Record the answer in quiz history
        if (quizHistoryId) {
            try {
                await fetch(`/api/quiz-history/${quizHistoryId}/answer`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        questionId: currentQuestion.id,
                        userAnswer: userAnswer,
                        isCorrect: correct,
                    }),
                });
            } catch (error) {
                console.error('Failed to record answer:', error);
            }
        }

        setTimeout(async () => {
            if (currentQuestionIndex < questions.length - 1) {
                setCurrentQuestionIndex((prev) => prev + 1);
                setUserAnswer('');
                setShowFeedback(false);
            } else {
                // Complete the quiz session
                if (quizHistoryId) {
                    try {
                        const response = await fetch(
                            `/api/quiz-history/${quizHistoryId}/complete`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                },
                            }
                        );

                        if (response.ok) {
                            const data = await response.json();
                            setFinalScore(data.quizHistory.score);
                        }
                    } catch (error) {
                        console.error(
                            'Failed to complete quiz session:',
                            error
                        );
                    }
                }
                setQuizCompleted(true);
            }
        }, 5000);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !showFeedback) {
            handleSubmitAnswer();
        }
    };

    if (loading) {
        return (
            <div className="container">
                <p className="loadingMessage">Loading quiz questions...</p>
            </div>
        );
    }

    if (questions.length === 0) {
        return (
            <div className="container">
                <p className="noQuestionsMessage">
                    No questions found for this quiz.
                </p>
            </div>
        );
    }

    if (quizCompleted) {
        return (
            <div className="container">
                <div className="completionContainer">
                    <h2 className="completionTitle">Quiz Complete!</h2>
                    {finalScore !== null && (
                        <p className="completionMessage">
                            Your score: {finalScore}%
                        </p>
                    )}
                    <p className="completionMessage">
                        You have completed all questions in this quiz.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="questionContainer">
                <h2 className="questionHeader">
                    Question {currentQuestionIndex + 1} of {questions.length}
                </h2>

                {currentQuestion && (
                    <div>
                        <p className="questionText">
                            {currentQuestion.question}
                        </p>

                        {!showFeedback && (
                            <div>
                                {currentQuestion.questionType === 1 ? (
                                    // Multiple Choice Question
                                    <div className="choicesContainer">
                                        {randomizedChoices.map(
                                            (choice, index) => {
                                                const letter =
                                                    String.fromCharCode(
                                                        65 + index
                                                    ); // A, B, C, ...
                                                return (
                                                    <button
                                                        key={index}
                                                        className={`choiceButton ${
                                                            userAnswer ===
                                                            choice
                                                                ? 'selected'
                                                                : ''
                                                        }`}
                                                        onClick={() =>
                                                            setUserAnswer(
                                                                choice
                                                            )
                                                        }
                                                    >
                                                        <span className="choiceLetter">
                                                            {letter}
                                                        </span>
                                                        <span className="choiceText">
                                                            {choice}
                                                        </span>
                                                    </button>
                                                );
                                            }
                                        )}
                                        <button
                                            className="submitButton"
                                            onClick={handleSubmitAnswer}
                                            disabled={!userAnswer.trim()}
                                        >
                                            OK
                                        </button>
                                    </div>
                                ) : currentQuestion.questionType === 2 ? (
                                    // True/False Question
                                    <div className="choicesContainer">
                                        <button
                                            className={`choiceButton ${
                                                userAnswer === 'True'
                                                    ? 'selected'
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                setUserAnswer('True')
                                            }
                                        >
                                            <span className="choiceLetter">
                                                T
                                            </span>
                                            <span className="choiceText">
                                                True
                                            </span>
                                        </button>
                                        <button
                                            className={`choiceButton ${
                                                userAnswer === 'False'
                                                    ? 'selected'
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                setUserAnswer('False')
                                            }
                                        >
                                            <span className="choiceLetter">
                                                F
                                            </span>
                                            <span className="choiceText">
                                                False
                                            </span>
                                        </button>
                                        <button
                                            className="submitButton"
                                            onClick={handleSubmitAnswer}
                                            disabled={!userAnswer.trim()}
                                        >
                                            OK
                                        </button>
                                    </div>
                                ) : (
                                    // Short Answer Question
                                    <div>
                                        <input
                                            type="text"
                                            className="answerInput"
                                            value={userAnswer}
                                            onChange={(e) =>
                                                setUserAnswer(e.target.value)
                                            }
                                            onKeyPress={handleKeyPress}
                                            placeholder="Enter your answer..."
                                            autoFocus
                                        />
                                        <button
                                            className="submitButton"
                                            onClick={handleSubmitAnswer}
                                            disabled={!userAnswer.trim()}
                                        >
                                            OK
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {showFeedback && (
                            <div className="feedbackContainer">
                                <p
                                    className={
                                        isCorrect
                                            ? 'resultCorrect'
                                            : 'resultIncorrect'
                                    }
                                >
                                    {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
                                </p>
                                <p className="correctAnswer">
                                    {currentQuestion.answer}
                                </p>
                                {currentQuestion.answerExplanation && (
                                    <div className="answerExplanation">
                                        <p className="explanationLabel">Explanation:</p>
                                        <p className="explanationText">
                                            {currentQuestion.answerExplanation}
                                        </p>
                                    </div>
                                )}
                                <p className="nextQuestionMessage">
                                    Moving to next question in 5 seconds...
                                </p>
                                <div className="progressBarContainer">
                                    <div
                                        className={`progressBarFill ${
                                            animateProgress ? 'animating' : ''
                                        }`}
                                        style={{
                                            width: animateProgress
                                                ? '0%'
                                                : '100%',
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
